import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import BookmarksRoundedIcon from '@mui/icons-material/BookmarksRounded'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded'
import FilterAltRoundedIcon from '@mui/icons-material/FilterAltRounded'
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded'
import HubOutlinedIcon from '@mui/icons-material/HubOutlined'
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded'
import TrackChangesRoundedIcon from '@mui/icons-material/TrackChangesRounded'
import { ChatContext } from '../../../context/chatContextCore'
import type { Workspace, WorkspaceRole } from '../../../context/WorkspaceContext'
import { getAnalysisDemoSnapshot } from '../../../explore_seeds/analysisDemoFixture'
import { usePersistedState } from '../../../hooks/usePersistedState'
import { useNls } from '../../../nls/nlsProvider'
import { useToast } from '../../../context/useToast'
import { analysisSnapshotService, type AnalysisSnapshotMetadata } from '../../../services/analysisSnapshotService'
import { subscribeAnalysisSavedViews, type AnalysisSavedView } from '../../../services/analysisSavedViewsService'
import { buildLocalAnalysisSnapshot } from '../../../services/ai/context/analysisDerivations'
import type { AnalysisIncludedSystemKey, AnalysisSnapshot, AnalysisSnapshotDoc, AnalysisTabKey, AnalysisTimeRangeKey, AnalysisViewMode } from '../../../types/analysis'
import type { TimelineElement } from '../../../types/timelineElement'
import { isPersonalWorkspace } from '../../../utils/workspaceKind'
import { useWorkspaceChecklistItems } from '../../../hooks/useWorkspaceChecklistItems'
import { useAccessRestriction } from '../../../hooks/useAccessRestriction'
import { EmptyHubState } from '../../Common/EmptyHubState'
import { Button, FilterChip, Pressable, StatusChip } from '../../Common'
import { type MobileTabItem } from '../../Common/MobileTabs'

import { MemberNexusHero } from '../../Member/controls/MemberNexusPrimitives'
import { MemberBottomSheet } from '../../Member/controls/MemberControlsPrimitives'
import { useMemberLayout } from '../../Member/MemberLayoutContext'
import { ChartPopoverProvider } from '../popover/ChartPopoverProvider'
import { ANALYSIS_SYSTEMS } from '../sidecar/AnalysisFilterControlsCard'
import { type AnalysisNavigate, OperationsTab, OverviewTab, TeamTab, TIME_RANGES, WorkTab } from '../tabs'
import { analysisHealthTone } from '../analysisHealthTone'

type MemberAnalysisTabKey = Extract<AnalysisTabKey, 'OVERVIEW' | 'WORK' | 'TEAM' | 'OPERATIONS'>

type MemberAnalysisViewProps = {
  workspace: Workspace
  role: WorkspaceRole
  elements: TimelineElement[]
  canViewFullAnalysis: boolean
  onNavigate: AnalysisNavigate
  resolveMemberLabel?: (uid: string) => string | undefined
}

const buildTabItems = (t: ReturnType<typeof useNls>['t']): Array<MobileTabItem<MemberAnalysisTabKey>> => [
  { key: 'OVERVIEW', label: t('NLSAnalysisTabOverview'), icon: <InsightsRoundedIcon />, activeIcon: <InsightsRoundedIcon />, testId: 'member-analysis-tab-overview' },
  { key: 'WORK', label: t('NLSAnalysisTabWork'), icon: <TrackChangesRoundedIcon />, activeIcon: <TrackChangesRoundedIcon />, testId: 'member-analysis-tab-work' },
  { key: 'TEAM', label: t('NLSAnalysisTabTeam'), icon: <GroupsRoundedIcon />, activeIcon: <GroupsRoundedIcon />, testId: 'member-analysis-tab-team' },
  { key: 'OPERATIONS', label: t('NLSAnalysisTabOperations'), icon: <HubOutlinedIcon />, activeIcon: <HubOutlinedIcon />, testId: 'member-analysis-tab-operations' },
]

const timeRangeSheetLabels = {
  '24H': 'NLSAnalysisMobileTime24Hours',
  '7D': 'NLSAnalysisMobileTime7Days',
  '30D': 'NLSAnalysisMobileTime30Days',
  '90D': 'NLSAnalysisMobileTime90Days',
  '12M': 'NLSAnalysisMobileTime12Months',
} as const

export const MemberAnalysisView = ({ workspace, role, elements, canViewFullAnalysis, onNavigate, resolveMemberLabel }: MemberAnalysisViewProps) => {
  const { t } = useNls()
  const { showToast } = useToast()
  const { restricted: isAccessRestricted } = useAccessRestriction()
  const memberLayout = useMemberLayout()
  const chatContext = useContext(ChatContext)
  const [activeTab, setActiveTab] = usePersistedState<MemberAnalysisTabKey>('member.analysisTab', 'OVERVIEW')
  const [timeRange, setTimeRange] = useState<AnalysisTimeRangeKey>('7D')
  const [includeArchived, setIncludeArchived] = useState(false)
  const [highlightAnomalies, setHighlightAnomalies] = useState(false)
  const [includedSystems, setIncludedSystems] = useState<Set<AnalysisIncludedSystemKey>>(() => new Set(ANALYSIS_SYSTEMS.map((system) => system.key)))
  const [snapshotNonce] = useState(0)
  const [viewMode, setViewMode] = useState<AnalysisViewMode>('LIVE')
  const [snapshotOverride, setSnapshotOverride] = useState<AnalysisSnapshotDoc | null>(null)
  const [filterSheetOpen, setFilterSheetOpen] = useState(false)
  const [viewsSheetOpen, setViewsSheetOpen] = useState(false)
  const [timeSheetOpen, setTimeSheetOpen] = useState(false)
  const [lensSheetOpen, setLensSheetOpen] = useState(false)
  const [savedViews, setSavedViews] = useState<AnalysisSavedView[]>([])
  const [snapshotHistory, setSnapshotHistory] = useState<AnalysisSnapshotMetadata[]>([])
  useEffect(() => subscribeAnalysisSavedViews(workspace.id, setSavedViews), [workspace.id])
  useEffect(() => {
    if (isAccessRestricted) return
    return analysisSnapshotService.subscribeRecentSnapshots({ workspaceId: workspace.id, onChange: setSnapshotHistory })
  }, [workspace.id, isAccessRestricted])
  const memberLabelById = useMemo(() => new Map((chatContext?.members ?? []).map((member) => [member.uid, member.name])), [chatContext?.members])
  const memberRoles = useMemo(() => new Map((chatContext?.members ?? []).map((member) => [member.uid || member.id, member.role])), [chatContext?.members])
  const resolveLabel = useCallback((uid: string) => resolveMemberLabel?.(uid) ?? memberLabelById.get(uid) ?? null, [memberLabelById, resolveMemberLabel])
  const checklistItems = useWorkspaceChecklistItems(workspace.id)
  const resolvedChecklistItems = checklistItems.length > 0 ? checklistItems : undefined
  const liveSnapshot = useMemo(
    () =>
      workspace.previewKind === 'DEMO'
        ? getAnalysisDemoSnapshot({ workspaceId: workspace.id })
        : buildLocalAnalysisSnapshot(workspace.id, elements, { timeRange, includeArchived, resolveMemberLabel: resolveLabel, memberRoles, cohort: null, checklistItems: resolvedChecklistItems }),
    [elements, includeArchived, memberRoles, resolveLabel, resolvedChecklistItems, timeRange, workspace.id, workspace.previewKind],
  )
  const liveMiddleSnapshot = ('middle' in liveSnapshot ? liveSnapshot.middle : liveSnapshot) as AnalysisSnapshot
  const snapshot = (viewMode === 'SNAPSHOT' && snapshotOverride ? snapshotOverride : liveMiddleSnapshot) as AnalysisSnapshot
  const personalWorkspace = isPersonalWorkspace(workspace)
  const visibleTabs = buildTabItems(t).filter(
    (tab) => tab.key === 'OVERVIEW' || tab.key === 'WORK' || (!personalWorkspace && tab.key === 'TEAM') || (!personalWorkspace && canViewFullAnalysis && tab.key === 'OPERATIONS'),
  )
  const normalizedActiveTab = visibleTabs.some((tab) => tab.key === activeTab) ? activeTab : 'OVERVIEW'
  const activeTabItem = visibleTabs.find((tab) => tab.key === normalizedActiveTab) ?? visibleTabs[0]
  const openReports = () => onNavigate('REPORTS')
  const toggleSystem = (system: AnalysisIncludedSystemKey) =>
    setIncludedSystems((current) => {
      const next = new Set(current)
      if (next.has(system)) next.delete(system)
      else next.add(system)
      return next
    })
  const applyView = (view: AnalysisSavedView) => {
    setActiveTab(visibleTabs.some((tab) => tab.key === view.tabKey) ? (view.tabKey as MemberAnalysisTabKey) : 'OVERVIEW')
    setTimeRange(view.filters.timeRange)
    setIncludeArchived(view.filters.includeArchived)
    setHighlightAnomalies(view.filters.highlightAnomalies)
    setIncludedSystems(new Set(view.filters.includedSystems as AnalysisIncludedSystemKey[]))
    if (view.tabKey === 'GOVERNANCE' || view.tabKey === 'AI_INTELLIGENCE') showToast(t('NLSAnalysisMobileWebOnlyTab'), 'ghost')
    setViewsSheetOpen(false)
  }
  const selectSnapshot = async (snapshotId: string) => {
    if (isAccessRestricted) return
    const next = await analysisSnapshotService.getSnapshot({ workspaceId: workspace.id, snapshotId })
    if (next) {
      setSnapshotOverride(next)
      setViewMode('SNAPSHOT')
      setViewsSheetOpen(false)
    }
  }
  const selectTab = (nextTab: MemberAnalysisTabKey) => {
    setActiveTab(nextTab)
    memberLayout?.scrollStageToTop()
  }

  if (elements.length === 0 && workspace.previewKind !== 'DEMO') return <EmptyHubState appearance="card" title={t('NLSAnalysisMobileEmpty')} />

  const stage =
    normalizedActiveTab === 'WORK' ? (
      <WorkTab snapshot={snapshot} onNavigate={onNavigate} onOpenReports={openReports} includedSystems={includedSystems} elements={elements} />
    ) : normalizedActiveTab === 'TEAM' ? (
      <TeamTab
        workspace={workspace}
        role={role}
        timeRange={timeRange}
        elements={elements}
        snapshot={snapshot}
        snapshotNonce={snapshotNonce}
        onNavigate={onNavigate}
        onOpenReports={openReports}
        includedSystems={includedSystems}
        highlightAnomalies={highlightAnomalies}
        memberDense
      />
    ) : normalizedActiveTab === 'OPERATIONS' ? (
      <OperationsTab
        workspace={workspace}
        role={role}
        timeRange={timeRange}
        elements={elements}
        snapshot={snapshot}
        snapshotNonce={snapshotNonce}
        onNavigate={onNavigate}
        onOpenReports={openReports}
        includedSystems={includedSystems}
        highlightAnomalies={highlightAnomalies}
        memberDense
      />
    ) : (
      <OverviewTab variant="member-dense" snapshot={snapshot} summary={viewMode === 'SNAPSHOT' ? (snapshotOverride?.aiSummary ?? null) : null} onNavigate={onNavigate} onOpenReports={openReports} />
    )

  const controls = (
    <div className="memberAnalysisControls" data-ui-name="UI-MPBRD-ANL-TOOLS" data-testid="member-analysis-bottom-controls">
      <div className="memberAnalysisTools" aria-label={t('NLSAnalysisMobileToolsAria')}>
        <Pressable
          className="memberAnalysisToolButton memberAnalysisToolButton--time"
          aria-label={t('NLSAnalysisMobileTimeAria')}
          onClick={() => setTimeSheetOpen(true)}
          data-testid="member-analysis-time-button"
        >
          <span>{t(TIME_RANGES.find((range) => range.key === timeRange)?.labelKey ?? 'NLSAnalysisTimeRange7D')}</span>
          <KeyboardArrowDownRoundedIcon aria-hidden="true" />
        </Pressable>
        <Pressable
          className="memberAnalysisToolButton memberAnalysisToolButton--filters"
          aria-label={t('NLSAnalysisMobileFiltersWithCount', { count: includedSystems.size })}
          onClick={() => setFilterSheetOpen(true)}
          data-testid="member-analysis-filters-button"
        >
          <FilterAltRoundedIcon aria-hidden="true" />
          <span>{t('NLSAnalysisMobileFilters')}</span>
          <span className="memberAnalysisToolButton__count" aria-label={t('NLSAnalysisMobileFiltersWithCount', { count: includedSystems.size })}>{includedSystems.size}</span>
        </Pressable>
        <Pressable
          className="memberAnalysisToolButton memberAnalysisToolButton--views"
          aria-label={t('NLSAnalysisMobileViewsAria')}
          onClick={() => setViewsSheetOpen(true)}
          data-testid="member-analysis-views-button"
        >
          <BookmarksRoundedIcon aria-hidden="true" />
          <span>{t('NLSAnalysisMobileViews')}</span>
        </Pressable>
        <Pressable
          className="memberAnalysisToolButton memberAnalysisToolButton--lens"
          aria-label={activeTabItem.label}
          onClick={() => setLensSheetOpen(true)}
          data-testid="member-analysis-lens-button"
        >
          {activeTabItem.icon}
          <span>{activeTabItem.label}</span>
          <KeyboardArrowDownRoundedIcon aria-hidden="true" />
        </Pressable>
      </div>
    </div>
  )

  return (
    <ChartPopoverProvider workspace={workspace} viewMode={viewMode} snapshotId={snapshotOverride?.snapshotId} isReducedViewer={role === 'VIEWER' || !canViewFullAnalysis}>
      <div className="memberAnalysisView" data-ui-name="UI-MPBRD-ANL" data-testid="member-analysis-view">
        <MemberNexusHero
          eyebrow={t('NLSAnalysisPageSubtitle')}
          title={t('NLSAnalysisPageTitle')}
          rightSlot={<StatusChip tone={analysisHealthTone(snapshot.health.state)} size="sm">{snapshot.health.score}</StatusChip>}
        />
        {memberLayout?.bottomAddonTarget ? null : controls}
        {memberLayout?.bottomAddonTarget ? createPortal(controls, memberLayout.bottomAddonTarget) : null}
        {viewMode === 'SNAPSHOT' && snapshotOverride ? (
          <div className="memberAnalysisHistoricBar" role="status">
            <span>{t('NLSAnalysisMobileHistoricTimestamp', { timestamp: snapshotOverride.generatedAt.toLocaleString() })}</span>
            <Button
              variant="tertiary"
              size="sm"
              onClick={() => {
                setViewMode('LIVE')
                setSnapshotOverride(null)
              }}
            >
              {t('NLSAnalysisMobileBackToLive')}
            </Button>
          </div>
        ) : null}
        <div className="memberAnalysisView__stage">{stage}</div>
        <MemberBottomSheet open={timeSheetOpen} onClose={() => setTimeSheetOpen(false)} title={t('NLSAnalysisMobileTimeTitle')} maxHeightSvh={80} testId="member-analysis-time-sheet">
          <div className="memberAnalysisSheet memberAnalysisTimeSheet">
            {TIME_RANGES.map((range) => (
              <Pressable
                key={range.key}
                aria-pressed={timeRange === range.key}
                onClick={() => {
                  setTimeRange(range.key)
                  setTimeSheetOpen(false)
                }}
                data-testid={`member-analysis-time-option-${range.key}`}
              >
                <span>{t(timeRangeSheetLabels[range.key])}</span>
                {timeRange === range.key ? <CheckRoundedIcon aria-label={t('NLSAnalysisMobileTimeSelected')} /> : null}
              </Pressable>
            ))}
          </div>
        </MemberBottomSheet>
        <MemberBottomSheet
          open={filterSheetOpen}
          onClose={() => setFilterSheetOpen(false)}
          title={t('NLSAnalysisMobileFilterTitle')}
          maxHeightSvh={80}
          testId="member-analysis-filter-sheet"
          footer={(
            <div className="memberAnalysisFilterActions" data-testid="member-analysis-filter-actions">
              <Button variant="tertiary" size="sm" onClick={() => setIncludedSystems(new Set(ANALYSIS_SYSTEMS.map((system) => system.key)))} data-testid="member-analysis-select-all-systems">
                {t('NLSAnalysisMobileSelectAll')}
              </Button>
              <Button variant="tertiary" size="sm" onClick={() => setIncludedSystems(new Set())} data-testid="member-analysis-clear-systems">
                {t('NLSAnalysisMobileClearAll')}
              </Button>
            </div>
          )}
        >
          <div className="memberAnalysisSheet memberAnalysisFilterSheet">
            <Pressable className="memberAnalysisFilterToggle" role="switch" aria-checked={includeArchived} onClick={() => setIncludeArchived((value) => !value)}>
              <span>{t('NLSAnalysisMobileIncludeArchived')}</span>
              <span className="memberAnalysisFilterToggle__track" aria-hidden="true"><span /></span>
            </Pressable>
            <Pressable className="memberAnalysisFilterToggle" role="switch" aria-checked={highlightAnomalies} onClick={() => setHighlightAnomalies((value) => !value)}>
              <span>{t('NLSAnalysisMobileHighlightAnomalies')}</span>
              <span className="memberAnalysisFilterToggle__track" aria-hidden="true"><span /></span>
            </Pressable>
            <section className="memberAnalysisSystems" aria-label={t('NLSAnalysisIncludedSystemsHeading')}>
              <strong>{t('NLSAnalysisIncludedSystemsHeading')}</strong>
              <div className="memberAnalysisSystemGrid">
            {ANALYSIS_SYSTEMS.map((system) => (
              <FilterChip key={system.key} selected={includedSystems.has(system.key)} onClick={() => toggleSystem(system.key)} data-testid={`member-analysis-system-${system.key}`}>
                {t(system.labelKey)}
              </FilterChip>
            ))}
              </div>
            </section>
          </div>
        </MemberBottomSheet>
        <MemberBottomSheet open={viewsSheetOpen} onClose={() => setViewsSheetOpen(false)} title={t('NLSAnalysisMobileViewsTitle')} maxHeightSvh={80} testId="member-analysis-views-sheet">
          <div className="memberAnalysisSheet memberAnalysisViewsSheet">
            <section className="memberAnalysisViewsSheet__section" aria-labelledby="member-analysis-saved-views-heading">
              <strong id="member-analysis-saved-views-heading">{t('NLSAnalysisMobileSavedViews')}</strong>
              <div className="memberAnalysisViewsSheet__rows">
                {savedViews.map((view) => (
                  <Pressable key={view.id} onClick={() => applyView(view)}>
                    {view.name}
                  </Pressable>
                ))}
              </div>
            </section>
            {!isAccessRestricted && (
              <>
                <div className="memberAnalysisViewsSheet__divider" role="presentation" />
                <section className="memberAnalysisViewsSheet__section" aria-labelledby="member-analysis-snapshots-heading">
                  <strong id="member-analysis-snapshots-heading">{t('NLSAnalysisMobileSnapshots')}</strong>
                  <div className="memberAnalysisViewsSheet__rows">
                    {snapshotHistory.map((item) => (
                      <Pressable key={item.snapshotId} onClick={() => void selectSnapshot(item.snapshotId)}>
                        {item.generatedAt.toLocaleString()}
                      </Pressable>
                    ))}
                  </div>
                </section>
              </>
            )}
          </div>
        </MemberBottomSheet>
        <MemberBottomSheet open={lensSheetOpen} onClose={() => setLensSheetOpen(false)} title={t('NLSAnalysisPageTitle')} maxHeightSvh={80} testId="member-analysis-lens-sheet">
          <div className="memberAnalysisSheet memberAnalysisLensSheet">
            {visibleTabs.map((tab) => {
              const isSelected = normalizedActiveTab === tab.key
              return (
                <Pressable
                  key={tab.key}
                  aria-pressed={isSelected}
                  onClick={() => {
                    selectTab(tab.key)
                    setLensSheetOpen(false)
                  }}
                  data-testid={`member-analysis-lens-option-${tab.key.toLowerCase()}`}
                >
                  <span className="memberAnalysisLensOption__label flex items-center gap-2" data-testid={tab.testId}>
                    {tab.icon}
                    <span>{tab.label}</span>
                  </span>
                  {isSelected ? <CheckRoundedIcon aria-label={t('NLSAnalysisMobileTimeSelected')} /> : null}
                </Pressable>
              )
            })}
          </div>
        </MemberBottomSheet>
      </div>
    </ChartPopoverProvider>
  )
}
